import type { TemplateActivity } from '@/types/tripTemplates';

export type TemplateActivityLocationMapping = {
  geocodeHint?: string;
  venueName?: string;
  notesWithVenue?: string;
};

function looksLikeCityRegionHint(value: string): boolean {
  const raw = value.trim();
  if (!raw) return false;
  // Typical city/region patterns: "Toronto", "Toronto, ON", "Chicago, Illinois", "London, UK"
  const commaParts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (commaParts.length === 1) {
    // Single token city names should be short-ish words, not long venue phrases.
    const words = raw.split(/\s+/).filter(Boolean);
    if (words.length <= 2 && raw.length <= 28) return true;
    return false;
  }
  if (commaParts.length > 3) return false;
  const regionPart = commaParts[1] || '';
  if (!regionPart) return false;
  // Accept short codes ("ON", "IL", "UK") or reasonable region/country names.
  return regionPart.length <= 24;
}

/**
 * Splits template activity location semantics:
 * - geocodeHint: city/region hint for map/geocoder context
 * - venueName: display label that should not pollute geocode queries
 * - notesWithVenue: preserves venue detail in itinerary notes when distinct
 */
export function mapTemplateActivityLocationFields(
  activity: Pick<TemplateActivity, 'venueName' | 'geocodeHint' | 'location' | 'notes'>,
  fallbackCityRegionHint?: string,
): TemplateActivityLocationMapping {
  const explicitGeocodeHint = (activity.geocodeHint || '').trim();
  const legacyLocation = (activity.location || '').trim();
  const fallbackHint = (fallbackCityRegionHint || '').trim();

  const locationIsCityLike = looksLikeCityRegionHint(legacyLocation);
  const geocodeHint = explicitGeocodeHint
    || (legacyLocation && locationIsCityLike ? legacyLocation : '')
    || fallbackHint
    || undefined;
  const venueName = (activity.venueName || '').trim() || legacyLocation || undefined;
  const notesWithVenue =
    venueName && venueName !== geocodeHint
      ? [venueName, activity.notes]
          .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
          .join('\n')
      : activity.notes;

  return {
    geocodeHint,
    venueName,
    notesWithVenue,
  };
}
